import { FC } from 'react';
import { ICON_NAMES, IPlanCard } from '@belong/types';

import { stringOrRichTextToString } from '@belong/contentful';
import { Icon } from '../../Icon';
import { Stack } from '../../ui';
import { AccordionList } from '../../lists';
import * as styles from './PlanCard.styles';
import { CostBreakdown } from './CostBreakdown/CostBreakdown';
import { PlanCTA } from './PlanCTA/PlanCTA';
import { SpeedBreakdown } from './SpeedBreakdown/SpeedBreakdown';
import { findElementSystemIconFields, getAccordionRowListItems } from './utils';
import { PLAN_CARD_TEST_ID } from './testIds';
import { PlanOffersAndAlerts } from './PlanOffersAndAlerts/PlanOffersAndAlerts';
import { createRefHandler } from './ControlledHeightContainer';
import { IMultipleAccordionPlanCardProps, IPlanCardBody } from './types';

export const PlanCardBodyNBN: FC<IPlanCard & IMultipleAccordionPlanCardProps & IPlanCardBody> = props => {
  const {
    planType,
    title,
    plan,
    dataBeforePromo,
    priceBeforePromo,
    typicalSpeedsCopy,
    inclusions,
    addOns,
    suitability,
    activityLevel,
    buttonLabel,
    accordionRefs,
    toggleAccordions,
    columnIndex = 0,
    accordionRowContentRefs,
    focusTextOnSelect,
    planGroupIncludesPromo,
    selectionStatus,
    buttonUrl,
    onPlanCardCtaClick,
    activityLevelRefs,
    activityLevelHeight = '0px',
    removeActivityLevelHeight,
    overrideButtonLabel = buttonLabel
  } = props;

  const forcedActivityLevelHeight = activityLevelHeight && !removeActivityLevelHeight ? activityLevelHeight : undefined;

  const activityLevelText = activityLevel && stringOrRichTextToString(activityLevel.html);

  const elementSystemIconFields = activityLevel && findElementSystemIconFields(activityLevel.html.content);
  const iconName = elementSystemIconFields && elementSystemIconFields[0]?.icon;

  // split the activityLevelText into an an array
  const activityLevelTextArray = activityLevelText?.split('\n');

  return (
    <Stack>
      <styles.FullHeightStack alignX="center" divider space={{ xs: 'large', md: 'xlarge' }}>
        <CostBreakdown
          data-testid={PLAN_CARD_TEST_ID.planCostBreakDown}
          planType={planType}
          plan={plan}
          dataBeforePromo={dataBeforePromo}
          priceBeforePromo={priceBeforePromo}
          alignBottom={!priceBeforePromo && planGroupIncludesPromo} // a plan that has a promo callout will have no offest
        />
        <PlanOffersAndAlerts {...props} />
        <Stack alignX="center" space={{ xs: 'medium', md: 'large' }}>
          <SpeedBreakdown
            planType={planType}
            plan={plan}
            typicalSpeedsCopy={typicalSpeedsCopy}
            data-testid={PLAN_CARD_TEST_ID.planSpeedBreakDown}
          />
          <styles.ActivityLevelContainer
            height={forcedActivityLevelHeight}
            ref={createRefHandler(columnIndex, activityLevelRefs)}
            data-testid={PLAN_CARD_TEST_ID.planActivityLevel}
          >
            {iconName && <Icon name={ICON_NAMES[iconName]} role="presentation" />}
            <dl>
              <dt>{activityLevelTextArray?.shift()}</dt>
              {activityLevelTextArray?.map(text => <dd key={text}>{text}</dd>)}
            </dl>
          </styles.ActivityLevelContainer>
        </Stack>
      </styles.FullHeightStack>

      <styles.FullHeightStack alignX="center" space={{ xs: 'small', md: 'medium' }} fillLastItem>
        <Stack alignX="center" space={{ xs: 'xxsmall', md: 'xsmall' }}>
          <Stack alignX="center">
            {inclusions && (
              <styles.AccordionsWrapper>
                <AccordionList
                  data-testid={PLAN_CARD_TEST_ID.planInclusions}
                  items={getAccordionRowListItems({
                    accordionRowList: inclusions,
                    accordionRefs,
                    columnIndex,
                    toggleAccordions,
                    accordionRowContentRefs
                  })}
                  variant="slim"
                  roundedCorners
                />
              </styles.AccordionsWrapper>
            )}
          </Stack>
          <Stack alignX="center">
            <styles.AccordionsWrapper>
              {addOns && (
                <AccordionList
                  data-testid={PLAN_CARD_TEST_ID.planAddons}
                  items={getAccordionRowListItems({
                    accordionRowList: addOns,
                    accordionRefs,
                    columnIndex,
                    toggleAccordions,
                    startIndex: inclusions?.length,
                    accordionRowContentRefs
                  })}
                  variant="slim"
                />
              )}
              {suitability && (
                <AccordionList
                  data-testid={PLAN_CARD_TEST_ID.planSuitability}
                  items={getAccordionRowListItems({
                    accordionRowList: suitability,
                    accordionRefs,
                    columnIndex,
                    toggleAccordions,
                    startIndex: (inclusions?.length ?? 0) + (addOns?.length ?? 0),
                    accordionRowContentRefs
                  })}
                  variant="slim"
                />
              )}
            </styles.AccordionsWrapper>
          </Stack>
        </Stack>

        <PlanCTA
          label={overrideButtonLabel}
          selectionStatus={selectionStatus}
          url={buttonUrl}
          onClick={onPlanCardCtaClick}
          className="cta-button"
          focusTextOnSelect={focusTextOnSelect}
          planName={title}
          data-testid={PLAN_CARD_TEST_ID.planCTAWrapper}
        />
      </styles.FullHeightStack>
    </Stack>
  );
};

PlanCardBodyNBN.displayName = 'PlanCardBodyNBN';
